import { palette } from "@brexhq/metal-foundation-shared";
import { css, type SerializedStyles } from "@emotion/react";
import styled from "@emotion/styled";

import breakpoints from "@brexhq/shared/styles/breakpoints";
import type { DefaultTheme } from "@brexhq/shared/styles/theming";

type StyleGetter = (props: { theme: DefaultTheme }) => SerializedStyles;

const desktopH1Styles: StyleGetter = ({ theme }) => css`
  font-family: ${theme.typography.TitleLargeSB.fontFamily};
  font-size: 64px;
  font-style: normal;
  font-weight: 700;
  line-height: 72px;
  letter-spacing: -2.56px;
`;

const mobileH1Styles: StyleGetter = ({ theme }) => css`
  ${theme.typography.TitleSmallSB}
  font-weight: 700;
`;

export const H1 = styled.h1`
  ${desktopH1Styles}
  margin: 0;
  ${breakpoints.SML} {
    ${mobileH1Styles}
  }
`;

const desktopH2Styles: StyleGetter = ({ theme }) => css`
  ${theme.typography.TitleLargeSB}
  letter-spacing: -1.75px;
`;
const mobileH2Styles: StyleGetter = ({ theme }) => css`
  ${theme.typography.HeadlineLargeSB}
  font-weight: 700;
`;
export const H2 = styled.h2`
  ${desktopH2Styles}
  margin: 0;
  ${breakpoints.SML} {
    ${mobileH2Styles}
  }
`;

const desktopH3Styles: StyleGetter = ({ theme }) => css`
  ${theme.typography.TitleSmallM}
  letter-spacing: -0.8px;
`;
const mobileH3Styles: StyleGetter = ({ theme }) => css`
  ${theme.typography.HeadlineSmallSB}
`;
export const H3 = styled.h3`
  ${desktopH3Styles}
  margin: 0;
  ${breakpoints.SML} {
    ${mobileH3Styles}
  }
`;

const desktopH4Styles: StyleGetter = ({ theme }) => css`
  ${theme.typography.HeadlineSmallSB}
  letter-spacing: -0.6px;
`;
const mobileH4Styles: StyleGetter = ({ theme }) => css`
  ${theme.typography.SubheadlineLargeSB}
`;
export const H4 = styled.h4`
  ${desktopH4Styles}
  margin: 0;
  ${breakpoints.SML} {
    ${mobileH4Styles}
  }
`;

const desktopH5Styles: StyleGetter = ({ theme }) => css`
  ${theme.typography.SubheadlineLargeSB}
`;
const mobileH5Styles: StyleGetter = ({ theme }) => css`
  ${theme.typography.BodyLargeSB}
`;
export const H5 = styled.h5`
  ${desktopH5Styles}
  margin: 0;
  ${breakpoints.SML} {
    ${mobileH5Styles}
  }
`;

const desktopH6Styles: StyleGetter = ({ theme }) => css`
  ${theme.typography.BodyLargeSB}
`;
const mobileH6Styles: StyleGetter = ({ theme }) => css`
  ${theme.typography.BodySmallSB}
`;
export const H6 = styled.h6`
  ${desktopH6Styles}
  margin: 0;
  ${breakpoints.SML} {
    ${mobileH6Styles}
  }
`;

const desktopParagraphStyles: StyleGetter = ({ theme }) => css`
  ${theme.typography.SubheadlineLargeR}
`;
const mobileParagraphStyles: StyleGetter = ({ theme }) => css`
  ${theme.typography.BodySmallR}
`;
export const Paragraph = styled.p`
  ${desktopParagraphStyles}
  color: ${palette.neutrals.coolGrey12};
  ${breakpoints.SML} {
    ${mobileParagraphStyles}
  }
`;

const desktopTableTextStyles: StyleGetter = ({ theme }) => css`
  ${theme.typography.BodyLargeR}
`;
const mobileTableTextStyles: StyleGetter = ({ theme }) => css`
  ${theme.typography.DetailLargeR}
`;
export const TableText = styled.span`
  ${desktopTableTextStyles}
  ${breakpoints.SML} {
    ${mobileTableTextStyles}
  }
`;

const desktopLargeCardTitleStyles: StyleGetter = ({ theme }) => css`
  ${theme.typography.HeadlineLargeSB}
  letter-spacing: -0.1px;
`;
const mobileLargeCardTitleStyles: StyleGetter = ({ theme }) => css`
  ${theme.typography.HeadlineSmallSB}
`;
export const LargeCardTitle = styled.h3`
  ${desktopLargeCardTitleStyles}
  margin: 0;
  ${breakpoints.SML} {
    ${mobileLargeCardTitleStyles}
  }
`;

const desktopRegularCardTitleStyles: StyleGetter = ({ theme }) => css`
  ${theme.typography.HeadlineSmallSB}
  letter-spacing: -0.6px;
`;
const mobileRegularCardTitleStyles: StyleGetter = ({ theme }) => css`
  ${theme.typography.SubheadlineLargeSB}
`;
export const RegularCardTitle = styled.h3`
  ${desktopRegularCardTitleStyles}
  margin: 0;
  ${breakpoints.SML} {
    ${mobileRegularCardTitleStyles}
  }
`;

export const cardBodyTextVariant = "BodyLargeM";
export const CardBody = styled.p`
  margin: 0;
  ${({ theme }) => theme.typography[cardBodyTextVariant]}
`;

export const AccordionTitle = styled.span`
  ${desktopH4Styles}
  ${breakpoints.SML} {
    ${mobileH5Styles}
  }
`;

export const desktopLinkListTextVariant = "SubheadlineLargeR";
export const mobileLinkListTextVariant = "BodyLargeR";
export const LinkList = styled.a`
  text-decoration: none;
  ${({ theme }) => theme.typography[desktopLinkListTextVariant]}
  ${breakpoints.SML} {
    ${({ theme }) => theme.typography[mobileLinkListTextVariant]}
  }
  :hover {
    color: ${palette.neutrals.coolGrey11};
  }
`;
